import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from "uuid";

import {
  Add,
  DeleteForever
} from "@mui/icons-material";
import { Tooltip, Fab, Button } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  RollOption
} from "@dndModels";

import API from "@utilities/smart_api";

import StringBox from "@components/input/strings/StringBox";

interface Props {
  roll_options: RollOption[];
  change_time: number;
  onChange: (changed: RollOption[]) => void; 
  disabled?: boolean;
}

const api = API.getInstance();

export default function RollOptions({ 
  roll_options,
  change_time,
  onChange, 
  // onCancel, 
  // disabled = false
}: Props) {
  const [prev_change_time, setPrevChangeTime] = useState(-1);
  const [prev_roll_options, setPrevRollOptions] = useState<RollOption[]>(roll_options);
  const [reloading, setReloading] = useState(false);
  const [ping, setPing] = useState(false);
  const [parse_string, setParseString] = useState("");

  const parseRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (change_time !== prev_change_time) {
      setPrevRollOptions(roll_options);
      setPrevChangeTime(change_time);
    }
  }, [change_time, prev_change_time, roll_options]);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);

  const parse = () => {
    // console.log(parse_string);
    if (parseRef) {
      const new_roll_options: RollOption[] = [];
      const parse_html = parseRef.current as HTMLElement;
      const tr_finder = parse_html.getElementsByTagName("tr");
      if (tr_finder.length > 0) {
        for (let i = 0; i < tr_finder.length; ++i) {
          const tr = tr_finder[i];
          const td_finder = tr.getElementsByTagName("td");
          if (td_finder.length > 1) {
            const rolls_td = td_finder[0];
            const result_td = td_finder[1];
            if (rolls_td.textContent && result_td.textContent) {
              const roll_pieces = rolls_td.textContent.split("-");
              const min_roll = +roll_pieces[0];
              const max_roll = roll_pieces.length > 1 ? +roll_pieces[1] : min_roll;
              const option: RollOption = {
                true_id: uuidv4().toString(),
                min_roll,
                max_roll,
                result: result_td.textContent.trim()
              };
              new_roll_options.push(option);
            }
          }
        }
      } else {
        const li_finder = parse_html.getElementsByTagName("li");
        if (li_finder.length > 0) {
          for (let i = 0; i < li_finder.length; ++i) {
            const li = li_finder[i];
            if (li.textContent) {
              const option: RollOption = {
                true_id: uuidv4().toString(),
                min_roll: new_roll_options.length+1,
                max_roll: new_roll_options.length+1,
                result: li.textContent.trim()
              };
              new_roll_options.push(option);
            }
          }
        } else {
          const lines = parse_string.split("\n");
          if (lines.length > 0) {
            let max = 0;
            for (let i = 0; i < lines.length; ++i) {
              const line = lines[i].trim();
              if (line.length > 0) {
                if (line.includes("=")) {
                  const pieces = line.split("=");
                  if (pieces.length === 2) {
                    const num_str = pieces[0].trim();
                    const result = pieces[1].trim();
                    if (num_str.includes("or")) {
                      const number_pieces = num_str.split("or");
                      const min_roll = +number_pieces[0];
                      const max_roll = +number_pieces[1];
                      if (!isNaN(min_roll) && !isNaN(max_roll)) {
                        const option: RollOption = {
                          true_id: uuidv4().toString(),
                          min_roll,
                          max_roll,
                          result
                        };
                        new_roll_options.push(option);
                        max = max_roll;
                      }
                    } else if (num_str.includes("-")) {
                      const number_pieces = num_str.split("-");
                      const min_roll = +number_pieces[0];
                      const max_roll = +number_pieces[1];
                      if (!isNaN(min_roll) && !isNaN(max_roll)) {
                        const option: RollOption = {
                          true_id: uuidv4().toString(),
                          min_roll,
                          max_roll,
                          result
                        };
                        new_roll_options.push(option);
                        max = max_roll;
                      }
                    } else {
                      const max_roll = +num_str;
                      if (!isNaN(max_roll)) {
                        const option: RollOption = {
                          true_id: uuidv4().toString(),
                          min_roll: max_roll,
                          max_roll,
                          result
                        };
                        new_roll_options.push(option);
                        max = max_roll;
                      }
                    }
                  }
                } else {
                  const option: RollOption = {
                    true_id: uuidv4().toString(),
                    min_roll: max+1,
                    max_roll: max+1,
                    result: line.trim()
                  };
                  new_roll_options.push(option);
                  max++;
                }
              }
            }
          }
        }
      }
      onChange(new_roll_options);
      setReloading(true);
    }
  }

  return (
    <Grid container spacing={0} direction="row">
      { api.user && api.user.admin &&
        <>
          <Grid
            style={{ maxHeight: "300px", overflowY: "scroll" }}
            size={{
              xs: 9
            }}>
            <StringBox 
              name="Parser"
              value={parse_string}
              multiline
              onBlur={(changed: string) => {
                while (changed.includes("–")) {
                  changed = changed.replace("–","-");
                }
                setParseString(changed);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <Button variant="contained" color="primary"
              onClick={() => {
                parse();
              }}>
              Parse
            </Button>
          </Grid>
          <Grid
            style={{ maxHeight: "300px", overflowY: "scroll" }}
            size={{
              xs: 12
            }}>
            <div ref={parseRef} dangerouslySetInnerHTML={{ __html: parse_string }}></div>
          </Grid>
        </>
      }
      <Grid
        size={{
          xs: 12
        }}>
        <Tooltip title={`Add New Option`}>
          <Fab size="small" color="primary" style={{marginLeft: "8px"}}
            onClick={ () => {
              const new_roll_options = roll_options;
              const next_roll = new_roll_options.length === 0 ? 1 : Math.max(...new_roll_options.map(o => { return o.max_roll; })) + 1;

              new_roll_options.push({
                true_id: uuidv4().toString(),
                min_roll: next_roll,
                max_roll: next_roll,
                result: ""
              });
              onChange(new_roll_options);
              setReloading(true);
            }}>
            <Add/>
          </Fab>
        </Tooltip>
      </Grid>
      { !reloading && prev_roll_options.map((option, key) => {
        return (
          <Grid
            key={key}
            container
            spacing={0}
            direction="row"
            size={{
              xs: 12
            }}>
            <Grid
              size={{
                xs: 6
              }}>
              <StringBox
                name="Minimum Roll"
                value={`${option.min_roll}`}
                type="number"
                onBlur={(changed: string) => {
                  const new_roll_options = roll_options;
                  const opt_finder = new_roll_options.filter(o => 
                    o.true_id === option.true_id);
                  if (opt_finder.length === 1) {
                    const opt = opt_finder[0];
                    opt.min_roll = +changed;
                    opt.max_roll = Math.max(opt.min_roll, opt.max_roll);
                    onChange(new_roll_options);
                    setPing(!ping);
                  }
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <StringBox
                name="Maximum Roll"
                value={`${option.max_roll}`}
                type="number"
                onBlur={(changed: string) => {
                  const new_roll_options = roll_options;
                  const opt_finder = new_roll_options.filter(o => 
                    o.true_id === option.true_id);
                  if (opt_finder.length === 1) {
                    const opt = opt_finder[0];
                    opt.max_roll = +changed;
                    opt.max_roll = Math.max(opt.min_roll, opt.max_roll);
                    onChange(new_roll_options);
                    setPing(!ping);
                  }
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 10
              }}>
              <StringBox
                name="Result"
                value={option.result}
                multiline
                onBlur={(changed: string) => {
                  const new_roll_options = roll_options;
                  const opt_finder = new_roll_options.filter(o => 
                    o.true_id === option.true_id);
                  if (opt_finder.length === 1) {
                    opt_finder[0].result = changed;
                    onChange(new_roll_options);
                    setPing(!ping);
                  }
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 2
              }}>
              <Tooltip title={`Remove Option`}>
                <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                  onClick={ () => {
                    const new_roll_options = roll_options.filter(o => o.true_id !== option.true_id);
                    onChange(new_roll_options);
                    setReloading(true);
                  }}>
                  <DeleteForever/>
                </Fab>
              </Tooltip>
            </Grid>
          </Grid>
        );
      })}
    </Grid>
  );
}
